import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { aggridHeaderClass, SmallTitleSubRow, SmallTitleWithSubSpan } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import {
    CreateCellClickHandler,
    CustomCellClickAction,
    createCellCssClassRules,
    GridCellConfig,
    useRowSelectionHandler,
} from 'src/utils/grid/GridHandlerContent';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from 'src/utils/grid/gridStyleUtils';
import { defaultHRM1030_R01FileDto, HRM1030_R01FileDto } from './Dto_HRM1030_R01';
import { useHRM1030_R01CellChange } from './hooks/HRM1030_R01CellChange';
import { useHRM1030_R01Store } from './Store_HRM1030_R01';
import { formatHRM1030_R01Number, HRM1030_R01_FILE_ACCEPT, isHRM1030_R01Pdf } from './Util_HRM1030_R01';

/** 첨부에서 클릭·키보드 편집을 허용할 컬럼이다. 파일은 바꿀 수 없고 비고만 고친다. */
const EDITABLE_COLUMNS = ['remark'];
/** 컬럼 설정 저장과 복원에 쓰는 첨부 그리드 키이다. */
const COLUMN_STATE_KEY = 'HRM1030_R01-file-grid-column-state';
/** 공통 컬럼 복원 함수에 전달하는 첨부 그리드 제목이다. */
const HEADER_NAMES = {
    rowIndex: '', fileType: '형식', fileNm: '파일명 (클릭하면 크게 보기)', fileSize: '크기(KB)',
    download: '다운로드', remark: '비고',
};

/** 첨부 그리드 참조와 첨부 추가·열람·다운로드 함수이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM1030_R01FileDto> | null>; // 첨부 그리드
    onFilesAdd: (files: File[]) => void; // 끌어놓기·파일 선택으로 받은 파일 추가
    onFileOpen: (row: HRM1030_R01FileDto) => void; // 파일명 클릭 시 열람 (이미지 팝업 또는 PDF 새 탭)
    onFileDownload: (row: HRM1030_R01FileDto) => void; // 저장된 첨부 다운로드
}

/**
 * 선택한 교육의 수료증 첨부를 끌어놓기·파일 선택으로 추가하고, 목록에서 열람·다운로드·비고 수정한다.
 * 행 번호 칸의 +는 파일 선택 창을 열고, 신규 첨부의 -는 바로 지운다. 저장된 첨부는 액션바 삭제로 D 표시한다.
 * @param gridRef 첨부 그리드 참조
 * @param onFilesAdd 받은 파일을 검사하고 신규 첨부로 추가하는 함수
 * @param onFileOpen 첨부 열람 함수
 * @param onFileDownload 첨부 다운로드 함수
 * @returns 끌어놓기 영역과 첨부 그리드 JSX
 */
export const FileTable_HRM1030_R01 = ({ gridRef, onFilesAdd, onFileOpen, onFileDownload }: Props) => {
    // 화면 Store에서 교육 목록, 첨부 대상 교육, 첨부 목록·원본, 행 교체·제거 함수, 선택·D 표시, 컬럼·스크롤 상태를 가져온다.
    const { HRM1030_R01TrainingList, selectedHRM1030_R01Training, HRM1030_R01FileList, originalHRM1030_R01FileList,
        updateHRM1030_R01FileList, removeHRM1030_R01FileList,
        selectedHRM1030_R01FileList, setSelectedHRM1030_R01FileList, deletedHRM1030_R01FileList,
        addSelectedHRM1030_R01FileList, removeSelectedHRM1030_R01FileList,
        deletedHRM1030_R01TrainingList, fileColumnState, setFileColumnState, fileScrollTop, setFileScrollTop } = useHRM1030_R01Store();
    // 셀 값 변경을 첨부 목록에 반영하는 함수를 가져온다.
    const { handleFileCellChange } = useHRM1030_R01CellChange();
    // 숨겨 둔 파일 선택 input. [파일 선택] 버튼과 행 번호 칸 +에서 클릭한다.
    const fileInputRef = useRef<HTMLInputElement>(null);
    // true이면 파일을 끌어 영역 위에 올려 둔 상태라 테두리를 강조한다.
    const [isDragOver, setIsDragOver] = useState(false);
    // true이면 행 클릭을 선택으로 처리한다. +/- 버튼 직후에는 false가 되어 같은 클릭을 한 번 무시한다(작업지시와 같음).
    const [clickable, setClickable] = useState(true);
    // 첨부 대상 교육 행. 교육 목록에서 valueIndex로 찾는다.
    const targetTraining = useMemo(
        () => HRM1030_R01TrainingList.find(row => row.valueIndex === selectedHRM1030_R01Training),
        [HRM1030_R01TrainingList, selectedHRM1030_R01Training],
    );
    // 첨부 그리드에 표시할 목록: 첨부 대상 교육에 속한 첨부만 거른다.
    const targetFileList = useMemo(
        () => HRM1030_R01FileList.filter(row => row.trainingValueIndex === selectedHRM1030_R01Training),
        [HRM1030_R01FileList, selectedHRM1030_R01Training],
    );
    // 그리드 기본 설정: 행 ID, 행 번호 렌더러, 행·셀 CSS 규칙 생성 함수, 빈 행을 채운 표시 목록을 받는다.
    const {
        getRowId,
        renderIndexCell,
        createRowClassRules,
        createCellClassRules,
        paddedGridData,
    } = useGridSettingContent<HRM1030_R01FileDto>({
        gridRef,
        key: 'valueIndex',
        gridData: targetFileList,
        defaultGridData: defaultHRM1030_R01FileDto,
        selectedRow: selectedHRM1030_R01FileList,
        originalGridData: originalHRM1030_R01FileList,
        updateRow: updateHRM1030_R01FileList,
    });
    // true이면 대상 교육이 D 표시되어 첨부를 편집할 수 없다.
    const isTargetDeleted = !!targetTraining && deletedHRM1030_R01TrainingList.includes(String(targetTraining.valueIndex));
    /**
     * D 표시되지 않은 첨부의 비고만 편집할 수 있다.
     * @param row 확인할 첨부 행
     * @returns 편집할 수 있으면 true
     */
    const editable = useCallback(
        (row?: HRM1030_R01FileDto) => !!row && !isTargetDeleted && !deletedHRM1030_R01FileList.includes(String(row.valueIndex)),
        [isTargetDeleted, deletedHRM1030_R01FileList],
    );
    /** 파일 선택 창을 연다. 첨부 가능 여부 검사는 onFilesAdd에서 한다. */
    const openFileDialog = useCallback(() => fileInputRef.current?.click(), []);
    /**
     * 신규 첨부의 -는 저장 전 행을 즉시 제거한다.
     * @param row -를 누른 첨부 행
     */
    const handleRemoveRow = useCallback((row: HRM1030_R01FileDto) => {
        if (!row.inserted) return;
        // - 버튼을 누른 클릭이 행 선택으로 번지지 않게 한다(공통 RemoveRowHandler와 같음).
        setClickable(false);
        removeHRM1030_R01FileList(row.valueIndex);
        setSelectedHRM1030_R01FileList(selectedHRM1030_R01FileList.filter(id => id !== String(row.valueIndex)));
    }, [removeHRM1030_R01FileList, setSelectedHRM1030_R01FileList, selectedHRM1030_R01FileList]);
    /**
     * 공통 index 렌더러로 행 상태와 +(파일 선택), -(신규 첨부 제거) 버튼을 표시한다.
     * @param params 행 번호 셀 정보
     * @returns 번호 또는 I·U·D 표시와 +, - 버튼 JSX
     */
    const renderFileIndexCell = useCallback((params: ICellRendererParams<HRM1030_R01FileDto>) =>
        renderIndexCell(
            params,
            innerParams => {
                const flags = getRowStyleFlags(innerParams, {
                    deletedList: deletedHRM1030_R01FileList,
                    selectedList: selectedHRM1030_R01FileList,
                    dataList: targetFileList,
                });
                /** 공통 상태 함수가 받는 문자열 ID와 실제 숫자 ID를 맞춘다. */
                const rowId = String(innerParams.data?.valueIndex);
                return {
                    ...flags,
                    isDeleted: deletedHRM1030_R01FileList.includes(rowId),
                    isSelected: selectedHRM1030_R01FileList.includes(rowId),
                };
            },
            () => {
                // + 버튼을 누른 클릭이 행 선택으로 번지지 않게 한다(공통 InsertRowHandler와 같음).
                setClickable(false);
                openFileDialog();
            },
            handleRemoveRow,
        ), [renderIndexCell, deletedHRM1030_R01FileList, selectedHRM1030_R01FileList, targetFileList, openFileDialog, handleRemoveRow]);
    /** 행별 index 열과 첨부 정보 열을 정의한다. */
    const baseColumnDefs = useMemo<ColDef<HRM1030_R01FileDto>[]>(() => [
        ...getDefaultRowIndexColumnConfig(renderFileIndexCell),
        {
            headerName: '형식', colId: 'fileType', field: 'fileType', width: 64,
            // 빈 행은 비우고, PDF면 "PDF", 그 외는 "이미지"로 표시한다.
            valueFormatter: params => !params.data?.fileNm ? '' : isHRM1030_R01Pdf(params.data) ? 'PDF' : '이미지',
        },
        {
            headerName: '파일명 (클릭하면 크게 보기)', colId: 'fileNm', field: 'fileNm', width: 280,
            // 공통 Grid_Excel은 편집 불가 셀에서 onCellClicked를 부르지 않으므로, 칸 안의 버튼이 직접 열람 함수를 부른다(FileForm과 같은 방식).
            cellRenderer: (params: ICellRendererParams<HRM1030_R01FileDto>) => {
                if (params.node.rowPinned || !params.data?.fileNm) return null;
                const row = params.data;
                return (
                    <button type="button" className="w-full truncate text-left text-blue-600 underline hover:text-blue-800"
                        title={row.fileNm}
                        onClick={event => {
                            // 행 선택·셀 범위 선택으로 이벤트가 번지지 않게 막는다.
                            event.stopPropagation();
                            onFileOpen(row);
                        }}>
                        {row.fileNm}
                    </button>
                );
            },
        },
        {
            headerName: '크기(KB)', colId: 'fileSize', field: 'fileSize', width: 80,
            // byte를 KB로 바꿔 반올림한다.
            valueFormatter: params => params.data?.fileNm ? formatHRM1030_R01Number(Math.round((Number(params.value) || 0) / 1024)) : '',
        },
        {
            headerName: '다운로드', colId: 'download', width: 70,
            // 저장된 첨부(공통 파일ID 있음)에만 다운로드 버튼을 표시한다.
            cellRenderer: (params: ICellRendererParams<HRM1030_R01FileDto>) => {
                if (params.node.rowPinned || !params.data?.filePath || params.data.inserted) return null;
                const row = params.data;
                return (
                    <button type="button" className="text-white h-[20px] rounded-[4px] px-[6px] text-[11px] bg-blue-dark"
                        onClick={event => {
                            event.stopPropagation();
                            onFileDownload(row);
                        }}>
                        받기
                    </button>
                );
            },
        },
        {
            headerName: '비고', colId: 'remark', field: 'remark', width: 200,
            editable: params => editable(params.data), cellEditorParams: { maxLength: 1000 },
        },
    ], [renderFileIndexCell, editable, onFileOpen, onFileDownload]);
    // 기본 컬럼 순서. 의존성: 컬럼 정의가 바뀔 때만 다시 만든다.
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]);
    /** 저장된 첨부 그리드의 컬럼 배치를 공통 함수로 복원한다(PRD2010_R01 작업지시와 같은 형태 + 지운 컬럼 거르기). */
    const columnDefs = useMemo(() => {
        if (fileColumnState === null) return baseColumnDefs;
        // 예전에 저장된 컬럼 배치에 지금은 없는 컬럼(등록일시 insertDt)이 남아 있으면 공통 함수가 빈 컬럼으로 되살리므로,
        // 현재 컬럼 정의에 있는 컬럼의 배치만 복원한다.
        const currentColIds = baseColumnDefs.map(column => ('colId' in column ? column.colId : undefined));
        return GridCustomColumnDefs<HRM1030_R01FileDto>({
            baseColumnDefs,
            columnState: fileColumnState.filter(state => currentColIds.includes(state.colId)),
            headerNames: HEADER_NAMES,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [baseColumnDefs, fileColumnState]);
    /** 셀 표시 규칙: 비고만 편집 가능, 나머지는 회색. 처음 한 번만 만든다. */
    const cellCssConfig = useMemo<GridCellConfig>(() => ({
        disabledCells: ['fileType', 'fileSize'],
        insertedDisabledCells: [],
        commonDisabledCells: [],
        pointerCells: [...EDITABLE_COLUMNS, 'fileNm'],
        insertedPointerCells: [],
        commonPointerCells: [],
        alignLeftCells: ['fileNm', 'remark'],
    }), []);
    // 위 설정을 신규 행용·기존 행용 컬럼 목록으로 합친 결과. 의존성: 설정 객체
    const cellCssColumns = useMemo(() => createCellCssClassRules(cellCssConfig), [cellCssConfig]);
    // 모든 컬럼에 적용할 기본 설정에 셀 CSS 규칙을 더한 객체
    const cellCssDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        cellClassRules: createCellClassRules<HRM1030_R01FileDto>({
            idKey: 'valueIndex',
            selectedList: selectedHRM1030_R01FileList.map(Number),
            deletedList: deletedHRM1030_R01FileList.map(Number),
            ...cellCssColumns,
        }),
    }), [createCellClassRules, selectedHRM1030_R01FileList, deletedHRM1030_R01FileList, cellCssColumns]);
    // 셀 클릭 동작 목록: 비고 클릭은 셀 편집 시작. 파일명 열람은 칸 안의 버튼이 처리한다. 의존성: 편집 판단 함수
    const cellClickActions = useMemo<CustomCellClickAction<HRM1030_R01FileDto>[]>(() => [
        {
            validate: (field, row) => EDITABLE_COLUMNS.includes(field) && editable(row),
            // !: rowIndex와 field가 반드시 있다고 TypeScript에 알려 준다.
            action: event => event.api.startEditingCell({ rowIndex: event.rowIndex!, colKey: event.colDef.field! }),
        },
    ], [editable]);
    // 클릭한 셀에 맞는 동작을 찾아 실행하는 셀 클릭 핸들러
    const handleCellClick = CreateCellClickHandler(targetFileList, 'valueIndex', cellClickActions);
    // 공통 행 선택 훅은 valueIndex를 숫자 그대로 비교하므로, 문자열로 저장한 선택·D 표시 목록을 숫자로 바꿔 넘긴다.
    // 훅 인자 타입이 string[]이라 변환 단언을 쓴다(공통 훅은 수정하지 않음). 의존성: 각 목록
    const selectedFileIdsForHook = useMemo(
        () => selectedHRM1030_R01FileList.map(Number) as unknown as string[],
        [selectedHRM1030_R01FileList],
    );
    const deletedFileIdsForHook = useMemo(
        () => deletedHRM1030_R01FileList.map(Number) as unknown as string[],
        [deletedHRM1030_R01FileList],
    );
    // 공통 행 선택 핸들러(PRD2010_R01 작업지시와 같음). 클릭하면 그 첨부만 선택하고, 선택된 첨부를 다시 누르면 해제한다.
    // D 표시 첨부는 선택하지 않고, +/- 직후 클릭은 한 번 무시한다.
    const handleRowClick = useRowSelectionHandler<HRM1030_R01FileDto>({
        dataList: targetFileList,
        idKey: 'valueIndex',
        selectedList: selectedFileIdsForHook,
        deletedList: deletedFileIdsForHook,
        addSelected: addSelectedHRM1030_R01FileList,
        removeSelected: removeSelectedHRM1030_R01FileList,
        clickable, setClickable,
    });
    /**
     * 끌어놓은 파일을 받는다. 브라우저가 파일을 직접 여는 기본 동작을 막는다.
     * @param event 끌어놓기 이벤트
     */
    const handleDrop = (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();
        setIsDragOver(false);
        if (event.dataTransfer.files.length) onFilesAdd(Array.from(event.dataTransfer.files));
    };
    /** 하단 고정 행의 형식 칸에 현재 첨부 건수를 표시한다. */
    const pinnedBottomRow = useMemo(() => [{ fileType: `${targetFileList.length}건` }], [targetFileList.length]);
    // 선택·삭제·신규·수정 행 배경색 규칙
    const rowClassRules = createRowClassRules<HRM1030_R01FileDto>({
        idKey: 'valueIndex',
        selectedList: selectedHRM1030_R01FileList.map(Number),
        deletedList: deletedHRM1030_R01FileList.map(Number),
    });
    // 제목 오른쪽에 표시할 대상 교육명. ?.·||: 교육이 없으면 빈칸, 이름이 비면 "(새 교육)"
    const trainingName = targetTraining ? (targetTraining.trainNm || '(새 교육)') : '';

    return (
        // 영역 안을 클릭하면 마지막 작업 그리드를 첨부로 기록한다. 끌어놓기는 영역 전체에서 받는다.
        <section className={`flex flex-col h-full min-h-0 ${isDragOver ? 'outline outline-2 outline-blue-400' : ''}`}
            onClickCapture={() => useHRM1030_R01Store.getState().setActiveGridType('file')}
            onDragOver={event => {
                event.preventDefault();
                setIsDragOver(true);
            }}
            onDragLeave={event => {
                // 영역 안의 자식 요소로 옮겨 갈 때는 강조를 유지한다.
                if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
                setIsDragOver(false);
            }}
            onDrop={handleDrop}>
            {/* 우클릭 메뉴, 전체 선택·선택 해제, 컬럼 상태 저장을 담당하는 공통 감싸기 컴포넌트 */}
            <GridContextWrapper<HRM1030_R01FileDto>
                gridRef={gridRef}
                paddedGridData={paddedGridData}
                initSelected={() => setSelectedHRM1030_R01FileList([])}
                allSelected={() => setSelectedHRM1030_R01FileList(targetFileList
                    .filter(row => !deletedHRM1030_R01FileList.includes(String(row.valueIndex)))
                    .map(row => String(row.valueIndex)))}
                defaultRowData={defaultHRM1030_R01FileDto}
                baseColumnDefs={columnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={setFileColumnState}
                localStorageKey={COLUMN_STATE_KEY}>
                {/* 공통 영역 제목(파란 막대 + 제목). 오른쪽에 첨부 대상 교육명을 표시한다. */}
                <SmallTitleWithSubSpan title="수료증 첨부" subTitle={trainingName} />
                {/* 끌어놓기 안내와 [파일 선택] 버튼. 공통 보조 줄(SmallTitleSubRow)이 교육 이력 요약 줄과 같은
                    위·좌·우 테두리를 그려 제목과 그리드 사이를 잇는다. */}
                <SmallTitleSubRow className="gap-[8px] text-xs">
                    <span>여기에 수료증 이미지·PDF를 끌어다 놓거나</span>
                    <button type="button" className="text-white h-[20px] rounded-[4px] px-[8px] text-[11px] font-semibold bg-blue-dark"
                        onClick={openFileDialog}>
                        파일 선택
                    </button>
                    <span>· PNG/JPG/GIF/PDF · 10MB 이하 · 파일명을 클릭하면 크게 열람</span>
                    {/* 숨긴 파일 선택 input. 여러 파일을 한 번에 고를 수 있다. */}
                    <input ref={fileInputRef} type="file" multiple accept={HRM1030_R01_FILE_ACCEPT} className="hidden"
                        onChange={event => {
                            // ?.: 선택한 파일이 없으면 아무것도 하지 않는다.
                            const files = event.target.files ? Array.from(event.target.files) : [];
                            if (files.length) onFilesAdd(files);
                            // 같은 파일을 다시 골라도 change가 일어나도록 값을 비운다.
                            event.target.value = '';
                        }} />
                </SmallTitleSubRow>
                {/* 첨부 그리드: 행·셀 CSS 규칙, 행 선택, 파일명 클릭 열람, 비고 편집, 하단 건수 행을 연결한다. */}
                <Grid_Excel<HRM1030_R01FileDto>
                    ref={gridRef}
                    rowData={paddedGridData}
                    getRowId={getRowId}
                    columnDefs={columnDefs}
                    defaultColDef={cellCssDefaultColDef}
                    rowClassRules={rowClassRules}
                    onRowClicked={handleRowClick}
                    onCellClicked={handleCellClick}
                    onCellValueChanged={handleFileCellChange}
                    pinnedBottomRowData={pinnedBottomRow}
                    columnStateKey={COLUMN_STATE_KEY}
                    setColumnState={setFileColumnState}
                    getDefaultColumnState={() => defaultColumnState}
                    scrollTop={fileScrollTop}
                    setScrollTop={setFileScrollTop} />
            </GridContextWrapper>
        </section>
    );
};
